import React from 'react';
import styles from './styles.scss';
import cx from 'classnames';
import { IconButton } from '@mui/material';
import CloseIcon from 'ui/elements/icons/CloseIcon';
import ButtonList from 'ui/elements/buttons/ButtonList';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';

interface Props {
  dismissKey?: string;
  onClose?: () => void;
  className?: string;
  children: Children;
  icon?: JSX.Element;
  confirmButton?: JSX.Element;
  dismissButton?: JSX.Element;
  title?: string;
  color?: 'yellow' | 'blue' | 'blue-light' | 'indigo' | 'white' | 'green';
  layout?: 'row' | 'column';
  dismissOnConfirm?: boolean;
}

export default function InfoMessage(props: Props) {
  // Use internal state management only if dismissKey is provided (and no external onClose)
  const {
    isClosed: isMessageDismissed,
    closeMessage: handleDismiss,
    isLoading,
  } = useCloseableMessage(props.dismissKey ? `message-is-read-${props.dismissKey}` : 'dummy-key');

  // If onClose is provided, use external state management (parent controls visibility)
  const shouldRender = props.onClose ? true : !isLoading && !isMessageDismissed;
  const handleClose = props.onClose || handleDismiss;
  const showCloseButton = !!(props.dismissKey || props.onClose);

  return shouldRender ? (
    <div
      className={cx('u-fade-in--quick', props.className, styles.infoMessage, {
        [styles.infoMessageWhite]: props.color === 'white',
        [styles.infoMessageYellow]: props.color === 'yellow',
        [styles.infoMessageIndigo]: props.color === 'indigo',
        [styles.infoMessageBlueLight]: props.color === 'blue-light',
        [styles.infoMessageGreen]: props.color === 'green',
      })}
    >
      {props.icon && (
        <div
          className={cx(styles.iconWrapper, {
            [styles.iconWrapperYellow]: props.color === 'yellow',
            [styles.iconWrapperWhite]: props.color === 'white',
            [styles.iconWrapperBlueLight]: props.color === 'blue-light',
            [styles.iconWrapperGreen]: props.color === 'green',
            [styles.columnLayout]: props.layout === 'column',
          })}
        >
          {props.icon && props.icon}
        </div>
      )}
      <div
        className={cx('u-flex-space-between u-flex-align-center', {
          [styles.columnLayout]: props.layout === 'column',
        })}
      >
        <div style={{ flex: 1 }} className={props.confirmButton ? 'u-half-spacing-right' : ''}>
          <div className={styles.title}>{props.title}</div>
          <div>{props.children}</div>
        </div>
        <ButtonList>
          <div onClick={props.dismissOnConfirm ? handleClose : undefined}>{props.confirmButton}</div>
          {showCloseButton &&
            (props.dismissButton ? (
              React.cloneElement(props.dismissButton, { onClick: handleClose })
            ) : (
              <IconButton color="inherit" onClick={handleClose} className="u-quarter-spacing-left" aria-label="Close">
                <CloseIcon />
              </IconButton>
            ))}
        </ButtonList>
      </div>
    </div>
  ) : null;
}
